import { addDisplayName, addProps, addWrapper, flowMax } from 'ad-hok'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  addLocation,
  addNavigate,
  addPathParam,
} from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'
import { addEffectOnMount } from 'ad-hok-utils'

import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { ConstrainWidth } from 'utils/layout'
import { addShowRegisterContext } from 'utils/register'
import { FLUID_CONTAINER_STYLE, serifFontFamily } from 'utils/styles'
import { setPageTitle } from 'utils/title'
import { PROFILE_HEADER_Z_INDEX } from 'utils/zIndex'
import { Header } from './Header'
import { NavBar } from 'components/navbar'
import { addBannerContext } from 'components/banners'
import { Banner } from 'components/banners/Banner'
import { GO2APP_URL } from '../../utils/urls'

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
  },
  profileContainer: {
    position: 'relative',
    zIndex: PROFILE_HEADER_Z_INDEX,
    transitionProperty: 'opacity',
    transitionDuration: '0.15s',
    scrollSnapAlign: 'start',
  },
  viewTabContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 18,
    marginBottom: 19,
  },
  slash: {
    fontFamily: serifFontFamily,
    fontSize: 20,
    letterSpacing: 0.01,
    color: '#fff',
    opacity: 0.4,
    paddingLeft: 12,
    paddingRight: 12,
  },
  viewTab: {
    fontFamily: serifFontFamily,
    fontSize: 20,
    letterSpacing: 0.01,
    color: '#fff',
    opacity: 0.4,
    textDecoration: 'none',
  },
  selectedViewTab: {
    color: '#fff',
    opacity: 1,
  },
  grayBackground: {
    background: '#2D2D2D',
    flex: 1,
  },
  italics: {
    fontStyle: 'italic',
  },
  wantMore: {
    scrollSnapAlign: 'end',
  },
  fluidContainer: {
    height: '100%',
    ...FLUID_CONTAINER_STYLE,
  },
})

interface Props {}
export const College: FC<Props> = flowMax(
  addDisplayName('College'),
  addPathParam('shortname'),
  setPageTitle(({ shortname }) => shortname),
  addWrapper((render, { shortname }) => (
    <React.Fragment key={shortname}>{render()}</React.Fragment>
  )),
  addApi,
  addShowRegisterContext,
  addDataLoadingFromPromise(
    'college',
    ({ api, shortname }) =>
      shortname ? api.colleges.getByShortname(shortname) : Promise.reject(),
    [],
    ({ shortname }) => `college-shortname-${shortname}`
  ),
  addLoadingAndErrorIndicators('college', {
    redirectTo404IfError: true,
  }),
  addNavigationState('scrollTo', 'setScrollTo', typedAs<number | null>(null)),
  addNavigate,
  addLocation,
  addBannerContext,
  addEffectOnMount(({ clearBanner }) => () => {
    clearBanner()
  }),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ classes, college, t }) => (
    <ConstrainWidth fillHeight className={classes.container}>
      <NavBar showLogo={true} />
      <div className={classes.profileContainer}>
        <Header college={college} />
        <div className={classes.viewTabContainer}></div>
      </div>

      <Banner
        banner={{
          unDismissable: true,
          noHider: true,
          textTranslationKey: 'banners.mobileOpenDownloadApp.appName',
          onCloseAnalyticsEvent: {
            options: {
              category: 'general',
              action: 'banner_open_download_in_app_close',
            },
          },
          link: {
            title: t('banners.mobileOpenDownloadApp.exclusiveTitle'),
            navigateTo: window.location.href.replace(
              `${window.location.protocol}//${window.location.hostname}${
                window.location.port ? ':' + window.location.port : ''
              }`,
              GO2APP_URL.replace(/\/$/, '')
            ),
            external: false,
            onClickAnalyticsEvent: {
              options: {
                category: 'general',
                action: 'banner_open_download_in_app_click',
              },
            },
          },
        }}
        onAction={() => {}}
      />
    </ConstrainWidth>
  )
)
